"use client"

import {
  type Adapter,
  WalletAdapterNetwork,
  WalletReadyState,
} from "@solana/wallet-adapter-base"
import {
  ConnectionProvider,
  WalletProvider,
} from "@solana/wallet-adapter-react"
import { WalletModalProvider } from "@solana/wallet-adapter-react-ui"
import {
  PhantomWalletAdapter,
  SolflareWalletAdapter,
  WalletConnectWalletAdapter,
} from "@solana/wallet-adapter-wallets"
import { clusterApiUrl } from "@solana/web3.js"
import { type FC, type ReactNode, useMemo } from "react"

// Default styles that can be overridden by your app
import "@solana/wallet-adapter-react-ui/styles.css"
import { settings } from "@src/components/DefuseSDK/constants/settings"
import { PROJECT_ID } from "@src/utils/environment"

export const SolanaWalletProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  // The network can be set to 'devnet', 'testnet', or 'mainnet-beta'.
  const network = WalletAdapterNetwork.Mainnet

  // You can also provide a custom RPC endpoint.
  const endpoint = useMemo(
    () => settings.rpcUrls.solana || clusterApiUrl(network),
    [network]
  )

  const wallets = useMemo(() => {
    /**
     * Wallets that implement either of these standards will be available automatically.
     *
     *   - Solana Mobile Stack Mobile Wallet Adapter Protocol
     *     (https://github.com/solana-mobile/mobile-wallet-adapter)
     *   - Solana Wallet Standard
     *     (https://github.com/anza-xyz/wallet-standard)
     *
     * If you wish to support a wallet that supports neither of those standards,
     * instantiate its legacy wallet adapter here. Common legacy adapters can be found
     * in the npm package `@solana/wallet-adapter-wallets`.
     */
    const walletAdapters: Adapter[] = [
      new PhantomWalletAdapter(),
      new SolflareWalletAdapter(),
    ]

    if (PROJECT_ID) {
      walletAdapters.push(
        new WalletConnectWalletAdapter({
          network,
          options: {
            projectId: PROJECT_ID,
          },
        })
      )
    }

    return walletAdapters
  }, [network])

  return (
    <ConnectionProvider endpoint={endpoint}>
      <WalletProvider wallets={wallets} autoConnect={shouldAutoConnect}>
        <WalletModalProvider>{children}</WalletModalProvider>
      </WalletProvider>
    </ConnectionProvider>
  )
}

/**
 * Gate wallet-adapter's autoConnect so it only fires for detected
 * extension wallets. Loadable adapters (Solflare's web-wallet SDK,
 * WalletConnect, mobile-only adapters) can navigate the page or load
 * iframes inside their connect() implementation — autoConnect would
 * trigger that on every page load, which is what was trapping users
 * on solflare.com after they tested Solflare without the extension.
 *
 * For Loadable wallets, require an explicit user click in the picker.
 */
async function shouldAutoConnect(adapter: Adapter): Promise<boolean> {
  return adapter.readyState === WalletReadyState.Installed
}
